import React, { useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { format } from 'date-fns';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import RefreshIcon from '@mui/icons-material/Refresh';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { useGetUserData } from '@hooks/useGetUserData';
import {
  useRecheckPageDeals,
  useRedoPageDealImage,
  useGetPageDeal
} from '@hooks/useGetPageDeal';
import { useRefreshPageDeal } from '@hooks/usePageDeals';
import { useSnackbar } from '@hooks/useSnackbar';
import { PageDealType } from '@types';
import {
  copyTextToClipboard,
  getPostPrefix,
  getPageDealUrlFromSlug,
  getTitleFromPageDeal,
  getPageDealBlogUrlFromSlug,
  getCurrentDealEventStrObj
} from '@utils/index';
import { iconButtonHoverRaiseStyle } from '@utils/styles';
import SendMailing from '../SendMailing';
import EditPageDeal from './components/EditPageDeal';
import SocialPageLink from './components/SocialPageLink';

const getLinkPrefix = () => {
  const currentEvent = getCurrentDealEventStrObj();
  return currentEvent?.copyLinkPrefix || getPostPrefix();
};

const AdminSlugActions = ({ pageDeal }: { pageDeal: PageDealType | null }) => {
  const { showMessage } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { data: user } = useGetUserData();
  const { mutate: redoPageDealImage } = useRedoPageDealImage();
  const { mutate: recheckAllDealsBySlug } = useRecheckPageDeals();
  const { mutate: refreshPageDeal } = useRefreshPageDeal();

  if (!user?.isAdmin || !pageDeal) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyLink = () => {
    copyTextToClipboard(getPostText());
    showMessage('Copied link to clipboard');
  };

  const handleCopyBlogLink = () => {
    copyTextToClipboard(getBlogPostText());
    showMessage('Copied link to clipboard');
  };

  const getPostText = () => {
    return `${getLinkPrefix()}${getTitleFromPageDeal(
      pageDeal
    )} — Updated ${format(new Date(), 'MMM. do, yyyy')}
${getPageDealUrlFromSlug(pageDeal?.slug)}`;
  };

  const getBlogPostText = () => {
    return `${getLinkPrefix()}${getTitleFromPageDeal(
      pageDeal
    )} — Updated ${format(new Date(), 'MMM. do, yyyy')}
${getPageDealBlogUrlFromSlug(pageDeal?.slug)}`;
  };

  const open = Boolean(anchorEl);

  const subject = `${getPostPrefix()}${getTitleFromPageDeal(
    pageDeal
  )} — Updated ${format(new Date(), 'MMM. do, yyyy')}`;

  return (
    <>
      <IconButton
        id="admin-slug-button"
        aria-label="Admin page options"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={(e) => {
          e.preventDefault();
          handleClick(e);
        }}
        size="small"
        sx={iconButtonHoverRaiseStyle}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
      >
        <EditPageDeal
          pageDeal={pageDeal}
          onCloseCallback={() => {
            handleClose();
          }}
        />
        <MenuItem
          key="recheck-page-deals"
          onClick={() => {
            recheckAllDealsBySlug(pageDeal.slug);
            showMessage(
              `Rechecking ${pageDeal.numLiveDeals} ${
                pageDeal.numLiveDeals === 1 ? 'deal' : 'deals'
              }`
            );
            handleClose();
          }}
        >
          <ListItemIcon>
            <PriceCheckIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Recheck all deals</ListItemText>
        </MenuItem>
        <SocialPageLink
          pageDeal={pageDeal}
          handleClose={() => {
            handleClose();
          }}
        />
        <MenuItem
          key="redo-image"
          onClick={() => {
            redoPageDealImage(pageDeal);
            showMessage('Redoing the page image');
            handleClose();
          }}
        >
          <ListItemIcon>
            <BrokenImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Redo image</ListItemText>
        </MenuItem>
        <MenuItem
          key="copy-link"
          onClick={() => {
            handleCopyLink();
            handleClose();
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Link</ListItemText>
        </MenuItem>
        <MenuItem
          key="copy-blog-link"
          onClick={() => {
            handleCopyBlogLink();
            handleClose();
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Blog Link</ListItemText>
        </MenuItem>
        <MenuItem
          key="refresh-page-deal"
          onClick={() => {
            refreshPageDeal(pageDeal?.slug);
            showMessage('Refreshing...');
            handleClose();
          }}
        >
          <ListItemIcon>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Refresh Page Deal</ListItemText>
        </MenuItem>
        <SendMailing
          appearance="menuItem"
          onCloseCallback={() => {
            handleClose();
          }}
          slug={pageDeal.slug}
          subject={subject}
        />
      </Menu>
    </>
  );
};

const AdminSlugActionsBySlugChild = ({ slug }: { slug: string }) => {
  const { data: pageDeal } = useGetPageDeal(slug);

  if (!pageDeal) {
    return null;
  }

  return <AdminSlugActions pageDeal={pageDeal.pageDeal} />;
};

export const AdminSlugActionsBySlug = ({ slug }: { slug: string }) => {
  const { data: user } = useGetUserData();

  if (!user?.isAdmin) {
    return null;
  }

  return <AdminSlugActionsBySlugChild slug={slug} />;
};

export default AdminSlugActions;
